'use client';

import type { Listing } from '@ps/models/lib/listing';
import { cn } from '@ps/ui/utils';

import {
  type VariantAttrType,
  type VariantCategory,
  type VariantSelection,
  type VariantValueType,
  buildVariantCategories,
} from '../functions/variantCategories';

type VariantPickerProps = {
  listing: Pick<Listing, 'variants' | 'skuAttributes' | 'quantity'>;
  selection: VariantSelection;
  onSelectAttr: (attr: VariantAttrType, value: VariantValueType) => void;
  /** When true, hides the picker entirely if there's only one variant. */
  hideSingleVariant?: boolean;
  /** When true, always shows per-value inventory counts. */
  alwaysShowQty?: boolean;
};

const MIN_INVENTORY_LEVEL_FOR_HIDE_QTY = 5;

/**
 * Mirrors RN `MultiVariantsView` + `VariantItem`. Renders one wrap-row of
 * pills per SKU attribute (Color, Size, …). Each pill shows the value and —
 * when stock is low or `alwaysShowQty` is set — the remaining inventory
 * ("(3 left)" / "Sold out" / "n/a").
 */
export function VariantPicker({
  listing,
  selection,
  onSelectAttr,
  hideSingleVariant,
  alwaysShowQty,
}: VariantPickerProps) {
  const categories = buildVariantCategories(listing, selection);

  if (categories.length === 0) return null;
  if (
    hideSingleVariant &&
    categories.length === 1 &&
    categories[0].items.length === 1
  ) {
    return null;
  }

  return (
    <section className="px-3.5">
      {categories.map((category, i) => (
        <VariantCategorySection
          key={category.attr}
          category={category}
          showCategoryName={categories.length > 1}
          alwaysShowQty={alwaysShowQty}
          onSelectAttr={onSelectAttr}
          className={i > 0 ? 'mt-2' : undefined}
        />
      ))}
    </section>
  );
}

function VariantCategorySection({
  category,
  showCategoryName,
  alwaysShowQty,
  onSelectAttr,
  className,
}: {
  category: VariantCategory;
  showCategoryName: boolean;
  alwaysShowQty?: boolean;
  onSelectAttr: (attr: VariantAttrType, value: VariantValueType) => void;
  className?: string;
}) {
  if (category.items.length === 0) return null;

  return (
    <div className={className}>
      {category.name || showCategoryName ? (
        <p className="text-label mb-2.5 font-bold text-[#444]">
          {category.name || `Variants Category`}
        </p>
      ) : null}
      <div className="flex flex-wrap">
        {category.items.map(item => {
          if (!item.value) return null;
          const selectable = item.matches.length > 0 && item.quantity > 0;
          const disabled = item.matches.length === 0;
          // RN's `qtyVisible` heuristic: when the consumer doesn't override,
          // only render the qty hint when stock is low (≤ threshold).
          const qtyVisible =
            alwaysShowQty ??
            (item.quantity > 0 &&
              item.quantity <= MIN_INVENTORY_LEVEL_FOR_HIDE_QTY);
          const qtyText = disabled
            ? 'n/a'
            : item.quantity === 0
              ? 'Sold out'
              : `(${item.quantity} left)`;

          return (
            <button
              key={item.value}
              type="button"
              disabled={!selectable}
              onClick={() => onSelectAttr(category.attr, item.value)}
              className={cn(
                'mb-2 mr-2 flex min-h-[35px] items-center justify-center rounded-md border px-3.5 py-2.5',
                item.selected
                  ? 'border-2 border-zinc-700 bg-zinc-100'
                  : selectable
                    ? 'border-zinc-300 bg-transparent'
                    : 'border-zinc-200 bg-zinc-100',
              )}
            >
              <span
                className={cn(
                  'text-sub font-bold leading-[15px]',
                  selectable
                    ? 'text-black'
                    : disabled
                      ? 'text-zinc-400'
                      : 'text-zinc-500',
                  qtyVisible && !disabled ? 'mr-3.5' : '',
                )}
              >
                {item.value}
              </span>
              {qtyVisible && !disabled ? (
                <span
                  className={cn(
                    'text-label -ml-2.5 shrink-0 font-medium opacity-65',
                    item.selected ? 'text-black' : 'text-zinc-600',
                  )}
                >
                  {qtyText}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
